import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { PermissionService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalHelper } from '@helpers/modal-helper';
import { SendAgreementEmailModalComponent } from '../../components/send-agreement-email-modal/send-agreement-email-modal.component';
import {
    AgreementAttachmentForm,
    AgreementNoteRemindersForm,
    AgreementPaymentForm,
    AgreementPeriodForm,
    AgreementReminderForm,
    AgreementSharedInformationForm,
    FileUpload,
    FileUploadGroupForm,
    AgreementForm,
    RealEstateAgreementForm,
    AgreementRentalForm,
    AgreementInventoryForm
} from '../../feature/shared-agreement.model';
import { APP_ROUTES } from 'app/constants';
import { AgreementsRouteId } from '../../agreement.routes';
import { GetAgreementResponse } from '@services/api-client/responses';
import {
    fromNgbDate,
    toDateFromNgbDate,
    toDateISOStringFromDateTime,
    toNgbDateFromString
} from '@helpers/datetime-helper';
import { AgreementFile } from 'app/types/agreement-file.type';
import { UpdateAgreementRequest } from '@services/api-client/requests';
import { catchError, firstValueFrom, map, Observable, of, tap, throwError } from 'rxjs';
import { Permission } from 'app/enums';
import { CanComponentDeactivate } from '@guards/can-deactivate.guard';
import { TabsetComponent } from '@shared/components/tabset';
import { AgreementBaseComponent } from '../create-new-agreement/agreement-base.component';
import { AgreementFormHelper } from '../../feature/shared-agreement.helper';
import { RightPanelService } from '@services/right-panel.service';
import { AgreementActivityComponent } from '../../feature/components/activity/activity.component';
import { PdfViewerComponent } from '../../feature/components/pdf-viewer/pdf-viewer.component';
import { RightPanelMenuOption } from 'app/components/right-panel/right-panel.component';
import { AgreementNotesRemindersComponent } from '../../feature/components/notes-reminders/notes-reminders.component';
import { AgreementAttachmentComponent } from '../../feature/components/attachments/agreement-attachment.component';

@UntilDestroy()
@Component({
    selector: 'app-agreement',
    templateUrl: './agreement.component.html',
    styleUrl: './agreement.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgreementComponent extends AgreementBaseComponent implements OnInit, CanComponentDeactivate {
    @ViewChild('footerContainer', { read: ViewContainerRef, static: true }) protected footerContainer: ViewContainerRef;
    @ViewChild('tabset') public tabset: TabsetComponent;
    private readonly permissionService = inject(PermissionService);
    private readonly rightPanelService = inject(RightPanelService);

    public showPotentialValidationMessages = [false, false];

    public initialLoad = true;
    protected footerTemplateRef: TemplateRef<ElementRef>;

    public hasError = false;
    public accessDenied = false;

    private agreementId: string;
    public agreementCreated: string;
    public sentForRenegotiateDate: string;
    public currentPanelContent: 'activities' | 'pdf' | 'notes' | 'attachments' = 'pdf';

    public setFooterTemplate(templateRef: TemplateRef<ElementRef>) {
        this.footerTemplateRef = templateRef;
    }

    public ngOnInit() {
        this.agreementId = this.route.snapshot.paramMap.get(AgreementsRouteId.AgreementId);
        this.metadataService.clearHighlights();
        this.fetchData();
    }

    private fetchData() {
        this.apiClientService.getAgreement$(this.agreementId).subscribe({
            next: (response: GetAgreementResponse) => {
                if (response) {
                    this.agreementCreated = response.created;
                    this.sentForRenegotiateDate = response.agreementPeriodRenewals?.sentForRenegotiateDate;
                    this.initializeAgreementForm(response);
                } else {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.AGREEMENTS.TOAST_GET_ERROR.MESSAGE'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                    this.navigateToAgreementList();
                }

                this.initialLoad = false;
                this.goToReminderTab();
                this.checkAgreementFileChanged();
                this.initEvents();
                this.initializeRightPanel();
                this.changeDetectorRef.markForCheck();
            },
            error: (error: { status?: number; error?: { type?: string } }) => {
                const isAccessError = error?.status === 403;
                if (isAccessError) {
                    this.accessDenied = true;
                    this.initialLoad = false;
                    this.changeDetectorRef.markForCheck();
                } else {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.AGREEMENTS.TOAST_GET_ERROR.MESSAGE'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            }
        });
    }

    public get lastDateForNotice() {
        return AgreementFormHelper.getlastDateForNoticeToString(this.periodForm?.controls);
    }

    public get daysToLastDateForNotice() {
        if (!this.periodForm?.controls) return 0;
        const date = AgreementFormHelper.getNoticeDate(this.periodForm?.controls);
        if (!date) return 0;
        const now = new Date();
        const diff = date.getTime() - now.getTime();
        if (diff < 0) return 0;
        return Math.ceil(diff / (1000 * 3600 * 24));
    }

    public get warningDaysToLastDateForNotice() {
        return this.daysToLastDateForNotice <= 90;
    }

    public get showInfo() {
        return !this.isTerminated && this.periodForm?.controls.validTo?.value;
    }

    public get daysToLastDateForTermination() {
        if (!this.f.period?.controls.expiryDateAfterTermination?.value) return 0;
        const date = toDateFromNgbDate(this.f.period.controls.expiryDateAfterTermination.value);
        if (!date || date < new Date()) return 0;
        const now = new Date();
        const diff = date.getTime() - now.getTime();
        return Math.ceil(diff / (1000 * 3600 * 24));
    }

    public get lastDateForTermination() {
        if (!this.f.period?.controls.expiryDateAfterTermination?.value) return '-';
        const date = fromNgbDate(this.f.period.controls.expiryDateAfterTermination.value);
        return toDateISOStringFromDateTime(date);
    }

    public get isTerminated() {
        return !!this.periodForm?.controls.terminationDate.value;
    }

    public get terminated() {
        const terminated = fromNgbDate(this.periodForm.controls.terminationDate.value);
        return toDateISOStringFromDateTime(terminated);
    }

    public get isManaged() {
        return !!this.periodForm?.controls.managedDate.value;
    }

    public override get hasRiskAnalysis() {
        return !!this.detailsForm.controls.riskAnalysis.value;
    }

    public get managed() {
        const managed = fromNgbDate(this.periodForm.controls.managedDate.value);
        return toDateISOStringFromDateTime(managed);
    }

    protected initEvents() {
        this.periodForm?.controls.terminationDate.valueChanges.subscribe(() => {
            this.changeDetectorRef.markForCheck();
        });
    }

    private goToReminderTab() {
        this.route.queryParams.subscribe((params) => {
            const tabIndex = params['tab'] as number;
            if (tabIndex) {
                setTimeout(() => {
                    const firstTab = this.tabset.tabs.get(tabIndex);
                    this.tabset.selectTab(firstTab);
                });
            }
        });
    }

    public goToPeriodAndRenewalsTab(event: Event) {
        event?.stopPropagation();
        const tab = this.tabset.tabs.get(2);
        this.tabset.selectTab(tab);
    }

    private initializeAgreementForm(data: GetAgreementResponse) {
        this.agreementForm = new FormGroup<AgreementForm>({
            fileUpload: new FormGroup<FileUploadGroupForm>({
                file: new FormControl(this.getContractFile(data.files))
            }),
            details: new FormGroup<AgreementSharedInformationForm>({
                agreementTypeId: new FormControl(data.agreementTypeId, Validators.required),
                customerId: new FormControl(data.customerId, Validators.required),
                responsibleId: new FormControl(data.responsibleId, Validators.required),
                counterpartId: new FormControl(data.counterpartId, Validators.required),
                name: new FormControl(data.name),
                agreementNo: new FormControl(data.agreementNo),
                category: new FormControl(data.category, Validators.required),
                area: new FormControl(data.area, Validators.required),
                description: new FormControl(data.description),
                signer: new FormControl(data.signer),
                readers: new FormControl(data.readers),
                allReadersAccess: new FormControl(data.allReadersAccess),
                status: new FormControl(data.status),
                riskAnalysis: new FormControl(data.riskAnalysis)
            }),
            period: new FormGroup<AgreementPeriodForm>({
                commitmentPeriodTypeId: new FormControl(
                    data.agreementPeriodRenewals?.commitmentPeriodTypeId ?? 0,
                    Validators.required
                ),
                validFrom: new FormControl(
                    toNgbDateFromString(data.agreementPeriodRenewals?.validFrom),
                    Validators.required
                ),
                validTo: new FormControl(toNgbDateFromString(data.agreementPeriodRenewals?.validTo)),
                noticePeriod: new FormControl(data.agreementPeriodRenewals?.noticePeriod ?? 0, Validators.required),
                noticePeriodTypeId: new FormControl(
                    data.agreementPeriodRenewals?.noticePeriodTypeId ?? 0,
                    Validators.required
                ),
                terminationDate: new FormControl(toNgbDateFromString(data.agreementPeriodRenewals?.terminationDate)),
                noticePeriodAfterExtension: new FormControl(data.agreementPeriodRenewals?.noticePeriodAfterExtension),
                noticePeriodTypeIdAfterExtension: new FormControl(
                    data.agreementPeriodRenewals?.noticePeriodTypeIdAfterExtension ?? 0
                ),
                periodAfterExtension: new FormControl(data.agreementPeriodRenewals?.periodAfterExtension ?? 0),
                periodTypeIdAfterExtension: new FormControl(
                    data.agreementPeriodRenewals?.periodTypeIdAfterExtension ?? 0
                ),
                managedDate: new FormControl(toNgbDateFromString(data.agreementPeriodRenewals?.managedDate)),
                managedNote: new FormControl(''),
                expiryDateAfterTermination: new FormControl(
                    toNgbDateFromString(data.agreementPeriodRenewals?.expiryDateAfterTermination)
                ),
                replacedByAgreementId: new FormControl(data.agreementPeriodRenewals?.replacedByAgreementId),
                replacedByAgreementName: new FormControl(data.agreementPeriodRenewals?.replacedByAgreementName),
                replacedAgreementId: new FormControl(data.agreementPeriodRenewals?.replacedAgreementId),
                replacedAgreementName: new FormControl(data.agreementPeriodRenewals?.replacedAgreementName)
            }),
            reminder: new FormGroup<AgreementReminderForm>({
                recipients: new FormControl(data.agreementReminder?.recipients),
                reminderPeriod: new FormControl(data.agreementReminder?.reminderPeriod ?? 1),
                numOfReminders: new FormControl(data.agreementReminder?.numOfReminders ?? 1, Validators.required),
                escalatedRecipients: new FormControl(data.agreementReminder?.escalatedRecipients ?? [])
            }),
            notesReminders: new FormGroup<AgreementNoteRemindersForm>({
                notes: new FormControl(data.agreementNoteReminders)
            }),
            attachments: new FormGroup<AgreementAttachmentForm>({
                attachments: new FormControl(this.buildAgreementAttachmentForm(data.files))
            })
        });

        if (data.template.payment) {
            this.agreementForm.controls.payment = new FormGroup<AgreementPaymentForm>({
                paymentTerms: new FormControl(data.agreementPayment?.paymentTerms),
                customPaymentTerms: new FormControl(data.agreementPayment?.paymentTerms),
                value: new FormControl(data.agreementPayment?.value),
                currency: new FormControl(data.agreementPayment?.currency),
                invoiceIntervalId: new FormControl(data.agreementPayment?.invoiceIntervalId)
            });
        }
        if (data.template.realEstate) {
            this.agreementForm.controls.realEstate = new FormGroup<RealEstateAgreementForm>({
                propertyNo: new FormControl(data.agreementRealEstate?.propertyNo),
                apartmentNo: new FormControl(data.agreementRealEstate?.apartmentNo),
                size: new FormControl(data.agreementRealEstate?.size),
                typeOfPremises: new FormControl(data.agreementRealEstate?.typeOfPremises)
            });
        }
        if (data.template.rental) {
            this.agreementForm.controls.rental = new FormGroup<AgreementRentalForm>({
                employeeNo: new FormControl(data.agreementRental?.employeeNo),
                employeeName: new FormControl(data.agreementRental?.employeeName),
                grossSalaryDeduction: new FormControl(data.agreementRental?.grossSalaryDeduction),
                benefitValue: new FormControl(data.agreementRental?.benefitValue),
                additionalInsurance: new FormControl(data.agreementRental?.additionalInsurance),
                registrationNo: new FormControl(data.agreementRental?.registrationNo),
                carBrand: new FormControl(data.agreementRental?.carBrand),
                carModel: new FormControl(data.agreementRental?.carModel),
                modelYear: new FormControl(data.agreementRental?.modelYear),
                mileageAnnual: new FormControl(data.agreementRental?.mileageAnnual)
            });
        }
        if (data.template.inventory) {
            this.agreementForm.controls.inventory = new FormGroup<AgreementInventoryForm>({
                inventoryNumber: new FormControl(data.agreementInventory?.inventoryNumber),
                typeOfInventory: new FormControl(data.agreementInventory?.typeOfInventory),
                condition: new FormControl(data.agreementInventory?.condition),
                serialNumber: new FormControl(data.agreementInventory?.serialNumber),
                location: new FormControl(data.agreementInventory?.location),
                benefitValue: new FormControl(data.agreementInventory?.benefitValue),
                warranty: new FormControl(data.agreementInventory?.warranty),
                manufacturer: new FormControl(data.agreementInventory?.manufacturer),
                model: new FormControl(data.agreementInventory?.model),
                modelYear: new FormControl(data.agreementInventory?.modelYear),
                user: new FormControl(data.agreementInventory?.user),
                purchaseCost: new FormControl(data.agreementInventory?.purchaseCost)
            });
        }

        if (!this.permissionService.hasPermissions([Permission.WriteAgreement]) || data.readOnly) {
            this.agreementForm.disable();
        }

        if (
            this.agreementForm.controls.reminder.value.recipients == null ||
            this.agreementForm.controls.reminder.value.recipients?.length == 0
        ) {
            this.agreementForm.controls.reminder.controls.numOfReminders.disable();
            this.agreementForm.controls.reminder.controls.reminderPeriod.disable();
            this.agreementForm.controls.reminder.controls.escalatedRecipients.disable();
        }
    }

    public get disabled() {
        return this.agreementForm.disabled;
    }

    public cancel() {
        this.navigateToAgreementList();
    }

    public renegotiateAgreement(event: Event) {
        event.stopPropagation();
        const modalRef = this.modalService.open(SendAgreementEmailModalComponent);
        const modalInstance = ModalHelper.getComponentInstance<SendAgreementEmailModalComponent>(modalRef);
        modalInstance.agreementIds = [this.agreementId];
    }

    private navigateToAgreementList() {
        void this.router.navigate([`/${APP_ROUTES.AgreementList}`]);
    }

    private getContractFile(files: AgreementFile[]) {
        const file = files.filter((p) => p.agreementFileTypeId == 0)?.at(0);
        if (file) {
            return { id: file.id, fileName: file.fileName } as FileUpload;
        }
        return null;
    }

    private buildAgreementAttachmentForm(files: AgreementFile[]) {
        return files
            .filter((p) => p.agreementFileTypeId == 1)
            .map((file) => {
                return {
                    id: file.id,
                    fileName: file.fileName,
                    fileExtension: file.fileExtension,
                    created: file.created
                } as FileUpload;
            });
    }

    public save() {
        this.save$().subscribe();
    }

    public async saveAndWait() {
        return firstValueFrom(this.save$(false));
    }

    private getInvalidControls(formGroup: FormGroup, prefix = ''): string[] {
        const invalidControls: string[] = [];

        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            const fullPath = prefix ? `${prefix}.${key}` : key;

            if (control instanceof FormGroup) {
                invalidControls.push(...this.getInvalidControls(control, fullPath));
            } else if (control?.invalid) {
                const errors = control.errors ? Object.keys(control.errors).join(', ') : 'unknown';
                invalidControls.push(`${fullPath} (${errors})`);
            }
        });

        return invalidControls;
    }

    public save$(loadAfterStoring = true): Observable<void> {
        if (
            this.agreementForm.invalid ||
            (!this.agreementForm.dirty &&
                !this.f.payment?.dirty &&
                !this.f.realEstate?.dirty &&
                !this.f.rental?.dirty &&
                !this.f.inventory?.dirty)
        ) {
            // Debug: Log invalid controls
            if (this.agreementForm.invalid) {
                const invalidControls = this.getInvalidControls(this.agreementForm);
                console.error('Invalid form controls:', invalidControls);
            }
            return of(void 0);
        }
        const agreementId = this.route.snapshot.paramMap.get(AgreementsRouteId.AgreementId);

        let riskAnalysis = null;
        if (this.f.details.controls.riskAnalysis?.dirty && this.f.fileUpload?.value?.file?.url !== 'REMOVE') {
            riskAnalysis = this.f.details.value.riskAnalysis;
        }

        const agreeementRequest: UpdateAgreementRequest = {
            fileName: this.getFileName(this.f.fileUpload.value.file),
            tempPath: this.f.fileUpload?.value?.file?.url,
            agreementNo: this.f.details.value.agreementNo,
            name: this.f.details.value.name,
            description: this.f.details.value.description,
            customerId: this.f.details.value.customerId,
            counterpartId: this.f.details.value.counterpartId,
            responsibleId: this.f.details.value.responsibleId,
            area: this.f.details.value.area,
            category: this.f.details.value.category,
            signer: this.f.details.value.signer,
            readers: this.f.details.value.readers,
            allReadersAccess: this.f.details.value.allReadersAccess,
            riskAnalysis: riskAnalysis
        };

        const detailsObservable =
            this.agreementForm.controls.details.dirty || this.agreementForm.controls.fileUpload.dirty
                ? this.apiClientService.updateAgreement$(agreementId, agreeementRequest)
                : of(null);

        return detailsObservable.pipe(
            untilDestroyed(this),
            this.storeAttachments(agreementId),
            this.storeAgreementPeriod(agreementId),
            this.storeReminderDetails(agreementId),
            this.storePaymentDetails(agreementId),
            this.storeRealEstateDetails(agreementId),
            this.storeRentalDetails(agreementId),
            this.storeInventoryDetails(agreementId),
            this.storeNoteReminders(agreementId),
            tap(() => {
                if (loadAfterStoring) this.fetchData();
                else this.agreementForm.markAsPristine();
                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                const message = this.translateService.instant(
                    'FEATURES.AGREEMENTS.TOAST_UPDATE_SUCCESS.MESSAGE'
                ) as string;
                this.toastService.open(title, message, { type: 'success', progressBar: true });
            }),
            map((): void => void 0),
            catchError((error: unknown): Observable<void> => {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.AGREEMENTS.TOAST_UPDATE_ERROR.MESSAGE'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
                return throwError(() => error) as Observable<void>;
            })
        ) as Observable<void>;
    }
    private getFileName(file: FileUpload) {
        return file?.fileName ? file.fileName : '';
    }

    public get agreementName() {
        return this.f?.details.controls.name.value ?? '...';
    }

    public get title() {
        if (this.f?.details.controls.agreementTypeId.value === 13)
            return this.translateService.instant('FEATURES.AGREEMENTS.TITLE_INVENTORY_VIEW') as string;
        return this.translateService.instant('FEATURES.AGREEMENTS.TITLE_VIEW') as string;
    }

    private initializeRightPanel() {
        if (!this.agreementId) return;
        const menuOptions = this.getMenuOptions();

        // Restore the currently active panel instead of always showing PDF viewer
        switch (this.currentPanelContent) {
            case 'activities':
                this.showActivities(menuOptions);
                break;
            case 'notes':
                this.showNotes(menuOptions);
                break;
            case 'attachments':
                this.showAttachments(menuOptions);
                break;
            case 'pdf':
            default:
                this.showPdfViewer(menuOptions);
                break;
        }
    }

    private showActivities(menuOptions?: RightPanelMenuOption[]) {
        this.currentPanelContent = 'activities';

        const options = menuOptions || this.getMenuOptions();

        this.rightPanelService.showComponentWithMenu(
            this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_ACTIVITY') as string,
            AgreementActivityComponent,
            {
                agreementId: this.agreementId
            },
            options,
            false,
            true,
            true,
            'schedule'
        );

        this.changeDetectorRef.markForCheck();
    }

    private showPdfViewer(menuOptions?: RightPanelMenuOption[]) {
        this.currentPanelContent = 'pdf';

        const options = menuOptions || this.getMenuOptions();

        // Pass the existing file form to the PDF viewer
        const fileForm = this.f?.fileUpload;

        this.rightPanelService.showComponentWithMenu(
            this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_CONTRACT_FILE') as string,
            PdfViewerComponent,
            {
                file: fileForm,
                id: `pdf-viewer-${this.agreementId}`
            },
            options,
            false,
            true,
            true,
            'contract'
        );

        this.changeDetectorRef.markForCheck();
    }

    private showNotes(menuOptions?: RightPanelMenuOption[]) {
        this.currentPanelContent = 'notes';

        const options = menuOptions || this.getMenuOptions();

        this.rightPanelService.showComponentWithMenu(
            this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_NOTES') as string,
            AgreementNotesRemindersComponent,
            {
                noteRemindersForm: this.agreementForm.controls.notesReminders,
                agreementId: this.agreementId,
                autoSaveNotes: true
            },
            options,
            false,
            true,
            true,
            'edit_note'
        );

        this.changeDetectorRef.markForCheck();
    }

    private showAttachments(menuOptions?: RightPanelMenuOption[]) {
        this.currentPanelContent = 'attachments';

        const options = menuOptions || this.getMenuOptions();

        this.rightPanelService.showComponentWithMenu(
            this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_ATTACHMENT') as string,
            AgreementAttachmentComponent,
            {
                attachmentForm: this.agreementForm.controls.attachments
            },
            options,
            false,
            true,
            true,
            'attach_file'
        );

        this.changeDetectorRef.markForCheck();
    }

    private getMenuOptions(): RightPanelMenuOption[] {
        return [
            {
                icon: 'contract',
                name: 'PDF Viewer',
                action: () => this.showPdfViewer(),
                disabled: this.currentPanelContent === 'pdf',
                tooltip: this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_CONTRACT_FILE') as string
            },
            {
                icon: 'schedule',
                name: 'Activities',
                action: () => this.showActivities(),
                disabled: this.currentPanelContent === 'activities',
                tooltip: this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_ACTIVITY') as string
            },
            {
                icon: 'edit_note',
                name: 'Notes',
                action: () => this.showNotes(),
                disabled: this.currentPanelContent === 'notes',
                tooltip: this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_NOTES') as string
            },
            {
                icon: 'attach_file',
                name: 'Attachments',
                action: () => this.showAttachments(),
                disabled: this.currentPanelContent === 'attachments',
                tooltip: this.translateService.instant('FEATURES.AGREEMENTS.GROUP_TITLE_ATTACHMENT') as string
            }
        ];
    }
}
